import { useEffect } from "react";

import { useLexicalComposerContext } from "@lexical/react/LexicalComposerContext";
import {
  exportToEntities,
  exportToTelegramHTML,
  usePreviewStore,
} from "@tgpe/editor-core";
import type { EditorState } from "lexical";

const DEBOUNCE_MS = 120;

/**
 * Subscribes to editor changes and pushes (text, entities, html) into
 * editor-core's `usePreviewStore`. Debounced so typing stays smooth on
 * mid-range Android.
 *
 * Mounted once inside a `<LexicalComposer/>`.
 */
export function PreviewBridgePlugin(): null {
  const [editor] = useLexicalComposerContext();

  useEffect(() => {
    let timer: ReturnType<typeof setTimeout> | null = null;

    function flush(state: EditorState): void {
      const { text, entities } = exportToEntities(state);
      const html = exportToTelegramHTML(state);
      usePreviewStore.getState().updatePreview(text, entities, html, state);
    }

    flush(editor.getEditorState());

    const unregister = editor.registerUpdateListener(({ editorState }) => {
      if (timer) clearTimeout(timer);
      timer = setTimeout(() => {
        flush(editorState);
      }, DEBOUNCE_MS);
    });

    return () => {
      if (timer) clearTimeout(timer);
      unregister();
      usePreviewStore.getState().clear();
    };
  }, [editor]);

  return null;
}
